import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

const Why = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-mist">
      <header className="border-b border-calm-border bg-fern/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-5 flex justify-between items-center">
          <div 
            className="text-2xl font-semibold text-calm-text cursor-pointer"
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="border-calm-border text-forest hover:bg-fern"
          >
            Back to Home
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-20">
        <h1 className="text-5xl font-light text-calm-text mb-12">Why OHMNAI?</h1>

        <section className="mb-16">
          <h2 className="text-2xl font-light text-calm-text mb-6">The crisis of intention</h2>
          <div className="bg-fern rounded-xl p-8 border border-calm-border space-y-4">
            <p className="text-lg text-forest leading-relaxed">
              You care. About the climate, about fairness, about what kind of world your children inherit. But caring hasn't translated into consistent action—not because you're lazy, but because the tools are broken.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              Feeds optimize for engagement, not alignment. Platforms extract attention, not enable intention. You're left with guilt, burnout, or paralysis.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              The gap isn't your fault. It's architectural.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-light text-calm-text mb-6">An interface for alignment, not engagement</h2>
          <div className="bg-fern rounded-xl p-8 border border-calm-border space-y-4">
            <p className="text-lg text-forest leading-relaxed">
              OHMNAI is not a productivity app. It's not a habit tracker. It's not gamified self-optimization.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              It's a calm interface between what you care about and what you can realistically do. It respects your capacity, your boundaries, and your silence.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              When you request an action, you get one suggestion—not a feed, not a guilt loop. You can skip. You can ignore. You can export your data and leave. That's by design.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-light text-calm-text mb-6">Belonging without coercion</h2>
          <div className="bg-fern rounded-xl p-8 border border-calm-border space-y-4">
            <p className="text-lg text-forest leading-relaxed">
              The Circle isn't a premium tier. It's stewardship. Members support the platform's continued existence and help shape its future—not through voting, but through quiet influence.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              Free users aren't second-class. They have access to the core loop: values, capacity, suggestions. The Circle adds memory, pooled actions, and co-shaping tools for those who want to go deeper.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              No one is manipulated. No one is extracted from. No one is judged.
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-light text-calm-text mb-6">This is not a nonprofit, and not a product</h2>
          <div className="bg-fern rounded-xl p-8 border border-calm-border space-y-4">
            <p className="text-lg text-forest leading-relaxed">
              OHMNAI sustains itself through the Circle. It doesn't take venture capital. It doesn't optimize for growth at all costs. It doesn't pretend to be neutral.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              It's infrastructure for humans who want to live aligned with their values—without being optimized, surveilled, or sold.
            </p>
            <p className="text-lg text-forest leading-relaxed">
              If that resonates, start. If it doesn't, that's fine too. Silence is valid here.
            </p>
          </div>
        </section>

        <div className="flex justify-center pt-8">
          <Button
            onClick={() => navigate('/values-profile')}
            className="bg-forest hover:bg-forest-dark text-white px-8 py-6 text-base rounded-lg transition-colors"
          >
            Begin quietly <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Why;
